import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import axios from './lib/axiosClient';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import FloatingWorkOrderPanel from './FloatingWorkOrderPanel';
import Layout from './Layout';
import Dashboard from './Dashboard';
import BookingCalendar from './BookingCalendar';
import Customers from './Customers';
import Staff from './Staff';
import StaffCalendar from './StaffCalendar';
import Checkout from './Checkout';
import Settings from './Settings';
import ChangePassword from './ChangePassword';
import Billing from './Billing';
import Features from './Features';
import MyPlan from './MyPlan';
import Reminders from './Reminders';
import TreatmentNotes from './TreatmentNotes';
import NotificationLog from './NotificationLog';
import Reports from './Reports';
import Inventory from './Inventory';
import SmokeshopDashboard from './SmokeshopDashboard';
import SpaDashboard from './SpaDashboard';
import SalesHistory from './SalesHistory';
import Services from './Services';
import Import from "./Import";
import BusinessSettings from "./BusinessSettings";
import WorkOrders from './WorkOrders';
import Finance from './Finance';
import VendorsPage from './VendorsPage';
import PurchaseOrders from './PurchaseOrders';
import Vehicles from './Vehicles';
import ShopEstimates from './Estimates';
import ShopInvoices from './Invoices';
import TrackOrder from './TrackOrder';
import ShopQueue from './ShopQueue';
import GiftCards from "./GiftCards";
import Memberships from "./Memberships";
import Packages from "./Packages";
import RentalDashboard from './RentalDashboard';
import InPersonSignature from './InPersonSignature';
import RepairModule from './RepairModule';
import SalesModule from './SalesModule';
import RentalLanding from './RentalLanding';
import MiniMindsLanding from './MiniMindsLanding';
import PublicRentalStorefront from './PublicRentalStorefront';
import PublicQuotePage from './PublicQuotePage';
import Waitlist from "./Waitlist";
import Commissions from "./Commissions";
import AdminPanel from './AdminPanel';
import AdminDashboard from './AdminDashboard';
import { AdminBusinesses, AdminRevenue, AdminLeads, AdminOnboarding, AdminCampaigns, AdminActivity, AdminAnalytics, AdminSettings } from './AdminPages';
import SiteSettings, { AdminSiteManager } from './SiteSettings';
import PublicBooking from './PublicBooking';
import PublicEstimate from './PublicEstimate';
import PublicSite from './PublicSite';
import TVDisplay from './TVDisplay';
import CustomerPortal from './CustomerPortal';
import RentalClientPortal from './RentalClientPortal';
import ClientPhotos from './ClientPhotos';
import Demo from './Demo';
import DemoPage from './DemoPage';
import LandingPage from './LandingPage';
import AcceptInvite from './AcceptInvite';
import EmployeePortal from './EmployeePortal';
import SetupWizard from './SetupWizard';
import ResetPassword from './ResetPassword';
import { WorkOrderPanelProvider } from './contexts/WorkOrderPanelContext';
import PWAInstallPrompt from './PWAInstallPrompt';
import SMSCampaigns from './SMSCampaigns';
import './App.css';
import ServicesPage from './ServicesPage';
import PrivacyPolicy from './PrivacyPolicy';
import TermsAndConditions from './TermsAndConditions';
import SignupPage from './SignupPage';

// ============================================================================
// PUBLIC: Login / Signup
// ============================================================================
function LoginPage() {
  var { signIn, signUp, resetPassword, user } = useAuth();
  var [email, setEmail] = React.useState('');
  var [password, setPassword] = React.useState('');
  var [showPassword, setShowPassword] = React.useState(false);
  var [isSignUp, setIsSignUp] = React.useState(false);
  var [isForgot, setIsForgot] = React.useState(false);
  var [resetSent, setResetSent] = React.useState(false);
  var [businessName, setBusinessName] = React.useState('');
  var [businessType, setBusinessType] = React.useState('spa');
  var [phone, setPhone] = React.useState('');
  var [ownerName, setOwnerName] = React.useState('');
  var [error, setError] = React.useState('');
  var [loading, setLoading] = React.useState(false);

  if (user) return <Navigate to="/app" />;

  var handleSubmit = async function(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      var result;
      if (isForgot) {
        result = await resetPassword(email);
        if (result.error) throw new Error(result.error);
        setResetSent(true);
      } else if (isSignUp) {
        result = await signUp(email, password, { fullName: ownerName, businessName: businessName, businessType: businessType, phone: phone });
        if (result.error) throw new Error(result.error);
      } else {
        result = await signIn(email, password);
        if (result.error) throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: '#0a0a1a' }}>
      <div style={{ maxWidth: '420px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px', fontWeight: '900', color: '#111' }}>iV</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '800', margin: '0 0 6px' }}>IVision Marketing</h1>
          <p style={{ color: '#888', fontSize: '15px', margin: 0 }}>{isForgot ? 'Reset your password' : isSignUp ? 'Create your account' : 'Sign in to your account'}</p>
        </div>

        <div style={{ background: '#12121f', borderRadius: '20px', padding: '32px', border: '1px solid #1e1e35' }}>
          {error && (
            <div style={{ background: '#d6303115', color: '#ff6b6b', padding: '12px 16px', borderRadius: '10px', marginBottom: '20px', fontSize: '14px' }}>{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Reset sent success */}
            {isForgot && resetSent ? (
              <div>
                <div style={{ background: '#10b98120', borderRadius: 12, padding: '20px', marginBottom: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>📧</div>
                  <div style={{ color: '#10b981', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Check Your Email</div>
                  <div style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6 }}>
                    We sent a password reset link to <span style={{ color: '#fff', fontWeight: 600 }}>{email}</span>. Click the link in the email to set a new password.
                  </div>
                </div>
                <button type="button" onClick={function() { setIsForgot(false); setResetSent(false); setError(''); }}
                  style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: 'pointer' }}>
                  Back to Sign In
                </button>
              </div>
            ) : isForgot ? (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Email Address</label>
                  <input type="email" value={email} onChange={function(e) { setEmail(e.target.value); }} required
                    autoComplete="email"
                    style={{ width: '100%', padding: '12px 16px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="you@business.com" />
                </div>
                <button type="submit" disabled={loading} style={{
                  width: '100%', padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)',
                  border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '700',
                  cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
                }}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <button type="button" onClick={function() { setIsForgot(false); setError(''); }}
                    style={{ background: 'none', border: 'none', color: '#ff6b2b', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    ← Back to Sign In
                  </button>
                </div>
              </div>
            ) : (
            <>
            {isSignUp && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Your Name</label>
                  <input type="text" value={ownerName} onChange={function(e) { setOwnerName(e.target.value); }} required
                    autoComplete="name"
                    style={{ width: '100%', padding: '12px 16px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="Jane Smith" />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Business Name</label>
                  <input type="text" value={businessName} onChange={function(e) { setBusinessName(e.target.value); }} required
                    autoComplete="organization"
                    style={{ width: '100%', padding: '12px 16px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="Your Business Name" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Business Type</label>
                    <select value={businessType} onChange={function(e) { setBusinessType(e.target.value); }}
                      style={{ width: '100%', padding: '12px 16px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none' }}>
                      <option value="spa">Massage / Spa</option>
                      <option value="salon">Hair Salon / Barbershop</option>
                      <option value="med_spa">Med Spa / Aesthetics</option>
                      <option value="tattoo_studio">Tattoo / Piercing Studio</option>
                      <option value="smoke_shop">Smoke / Vape Shop</option>
                      <option value="motorcycle_shop">Motorcycle Repair</option>
                      <option value="auto_repair">Auto Repair / Detailing</option>
                      <option value="fitness">Fitness / Yoga Studio</option>
                      <option value="cleaning">Cleaning Service</option>
                      <option value="nails">Nail Salon</option>
                      <option value="rental">Equipment Rental</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Phone</label>
                    <input type="tel" value={phone} onChange={function(e) { setPhone(e.target.value); }}
                      autoComplete="tel"
                      style={{ width: '100%', padding: '12px 16px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="(702) 555-0123" />
                  </div>
                </div>
              </>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Email</label>
              <input type="email" value={email} onChange={function(e) { setEmail(e.target.value); }} required
                autoComplete="email"
                style={{ width: '100%', padding: '12px 16px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
                placeholder="you@business.com" />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={function(e) { setPassword(e.target.value); }} required
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  style={{ width: '100%', padding: '12px 16px', paddingRight: '48px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
                  placeholder={isSignUp ? 'Create password (6+ chars)' : 'Your password'} />
                <button type="button" onClick={function() { setShowPassword(!showPassword); }}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '14px', padding: '4px' }}>
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              {!isSignUp && (
                <div style={{ textAlign: 'right', marginTop: 6 }}>
                  <button type="button" onClick={function() { setIsForgot(true); setError(''); }}
                    style={{ background: 'none', border: 'none', color: '#888', fontSize: '13px', cursor: 'pointer', padding: 0 }}>
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)',
              border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '700',
              cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
            }}>{loading ? 'Please wait...' : isSignUp ? 'Get Started' : 'Sign In'}</button>
            </>
            )}
          </form>

          {!isForgot && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button onClick={function() { setIsSignUp(!isSignUp); setError(''); }}
              style={{ background: 'none', border: 'none', color: '#ff6b2b', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </button>
          </div>
          )}
        </div>

        <p style={{ textAlign: 'center', color: '#555', fontSize: '12px', marginTop: '20px' }}>
          No credit card required &middot; 14-day trial
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// HOME ROUTER: Super Admin → Command Center, New Clients → Wizard, Others → Dashboard
// ============================================================================
function HomeRouter() {
  var { isSuperAdmin, tenant, loading } = useTenant();
  var bt = (tenant?.business_type || '').toLowerCase();
  var isSpaType = ['spa','massage','salon','barbershop','barber','nails','nail_salon','hair_salon','beauty','medspa','med_spa'].includes(bt);

  // Wait for tenant to load before routing — prevents flash to wrong dashboard
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', background: '#0a0a1a' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg,#ff6b2b,#ffab40)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 900, color: '#111' }}>iV</span>
        </div>
        <p style={{ color: '#666', fontSize: 14 }}>Loading...</p>
      </div>
    </div>
  );

  // Super admin with no override → command center
  if (isSuperAdmin && !localStorage.getItem('sa_tenant_override')) return <AdminDashboard />;
  // Route by tenant's actual business type
  console.log('[HomeRouter] tenant:', tenant?.id, 'bt:', bt, 'override:', localStorage.getItem('sa_tenant_override'));
  if (bt === 'rental') {
    const p = window.location.pathname;
    if (p === '/repairs') return <RepairModule />;
    if (p === '/sales') return <SalesModule />;
    return <RentalDashboard />;
  }
  if (isSpaType) return <SpaDashboard />;
  return <Dashboard />;
}

// ============================================================================
// PROTECTED: Business Dashboard (all /app/* routes)
// ============================================================================
function SpaOrDashboard() {
  var { tenant, isSuperAdmin } = useTenant();
  var { user } = useAuth();
  var isSA = user?.email?.toLowerCase() === 'ivanjackm@protonmail.com';
  if (isSA && !localStorage.getItem('sa_tenant_override')) return <AdminDashboard />;
  var bt = (tenant?.business_type || '').toLowerCase();
  var isSpa = ['spa','massage','salon','barbershop','barber','nails','nail_salon','hair_salon','beauty','medspa','med_spa'].includes(bt);
  if (bt === 'rental') {
    const p = window.location.pathname;
    if (p === '/repairs') return <RepairModule />;
    if (p === '/sales') return <SalesModule />;
    return <RentalDashboard />;
  }
  if (isSpa) return <SpaDashboard />;
  return <Dashboard />;
}

function SmokeshopOrFinance() {
  var { tenant } = useTenant();
  var bt = (tenant?.business_type || '').toLowerCase();
  var isSmokeShop = ['smoke_shop','smoke','vape','dispensary'].includes(bt);
  if (isSmokeShop) return <SmokeshopDashboard defaultTab="finance" />;
  return <Finance />;
}

function AppRoutes() {
  var { isSuperAdmin } = useTenant();

  return (
    <Layout>
      <FloatingWorkOrderPanel />
      <Routes>
        {/* Home route - role-based */}
        <Route path="/" element={<SpaOrDashboard />} />

        {/* Business routes */}
        <Route path="/dashboard" element={<SpaOrDashboard />} />
        <Route path="/bookings" element={<BookingCalendar />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/staff" element={<Staff />} />
        <Route path="/schedule" element={<StaffCalendar />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/reminders" element={<Reminders />} />
        <Route path="/notifications" element={<NotificationLog />} />
        <Route path="/sms-campaigns" element={<SMSCampaigns />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/services" element={<Services />} />
        <Route path="/import" element={<Import />} />
        <Route path="/gift-cards" element={<GiftCards />} />
        <Route path="/memberships" element={<Memberships />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/client-photos" element={<ClientPhotos />} />
        <Route path="/business-settings" element={<BusinessSettings />} />
        <Route path="/work-orders" element={<WorkOrders />} />
        <Route path="/finance" element={<SmokeshopOrFinance />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/purchase-orders" element={<PurchaseOrders />} />
        <Route path="/treatment-notes" element={<TreatmentNotes />} />
        <Route path="/vehicles" element={<Vehicles />} />
        <Route path="/estimates" element={<ShopEstimates />} />
        <Route path="/invoices" element={<ShopInvoices />} />
        <Route path="/plan" element={<MyPlan />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/setup" element={<SetupWizard onComplete={function() { window.location.href = '/app'; }} />} />

        {/* Super Admin only */}
        <Route path="/site" element={<SiteSettings />} />
        {isSuperAdmin && <Route path="/admin/sites" element={<AdminSiteManager />} />}
        {isSuperAdmin && <Route path="/admin" element={<AdminDashboard />} />}
        {isSuperAdmin && <Route path="/admin/old" element={<AdminPanel />} />}
        {isSuperAdmin && <Route path="/admin/businesses" element={<AdminPanel />} />}
        {isSuperAdmin && <Route path="/admin/revenue" element={<AdminRevenue />} />}
        {isSuperAdmin && <Route path="/admin/leads" element={<AdminLeads />} />}
        {isSuperAdmin && <Route path="/admin/onboarding" element={<AdminOnboarding />} />}
        {isSuperAdmin && <Route path="/admin/campaigns" element={<AdminCampaigns />} />}
        {isSuperAdmin && <Route path="/admin/activity" element={<AdminActivity />} />}
        {isSuperAdmin && <Route path="/admin/analytics" element={<AdminAnalytics />} />}
        {isSuperAdmin && <Route path="/admin/settings" element={<AdminSettings />} />}

        {/* Smoke shop specific routes */}
        <Route path="/sales-history" element={<SalesHistory />} />
        <Route path="/rentals" element={<RentalDashboard />} />
        <Route path="/repairs" element={<RepairModule />} />
        <Route path="/sales" element={<SalesModule />} />
        <Route path="/rent" element={<PublicRentalStorefront />} />
        <Route path="/miniminds" element={<MiniMindsLanding />} />
        <Route path="/vendors" element={<SmokeshopDashboard defaultTab="vendors" />} />
        <Route path="/contacts" element={<SmokeshopDashboard defaultTab="contacts" />} />
        <Route path="/crm" element={<SmokeshopDashboard defaultTab="crm" />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/app" />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />

      </Routes>
    </Layout>
  );
}

function ProtectedApp() {
  var { user, loading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a1a' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px', fontWeight: '900', color: '#111' }}>iV</span>
          </div>
          <p style={{ color: '#888', fontSize: '15px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  return (
    <TenantProvider>
      <WorkOrderPanelProvider>
        <AppRoutes />
      </WorkOrderPanelProvider>
    </TenantProvider>
  );
}

// ============================================================================
// ROOT: Route Map
// ============================================================================

// Subdomain → storefront mapping
const SUBDOMAIN_STOREFRONTS = {
  'onthegrind': { type: 'rental', component: 'rental' },
};

function App() {
  // Detect subdomain and short-circuit to storefront if needed
  const hostname = window.location.hostname; // e.g. onthegrind.ivisionmarketing.com
  const subdomain = hostname.split('.')[0];
  const storefront = SUBDOMAIN_STOREFRONTS[subdomain];
  if (storefront?.type === 'rental') {
    return <PublicRentalStorefront />;
  }

  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public */}
          <Route path="/" element={<RentalLanding />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/rentals" element={<RentalLanding />} />
        <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/features" element={<Features />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/employee" element={<EmployeePortal />} />
          <Route path="/employee/:subdomain" element={<EmployeePortal />} />

          {/* Customer-facing */}
          <Route path="/book/:subdomain" element={<PublicBooking />} />
          <Route path="/tv/:subdomain" element={<TVDisplay />} />
          <Route path="/portal/:subdomain" element={<CustomerPortal />} />
          <Route path="/portal/:subdomain/rental" element={<RentalClientPortal />} />
          <Route path="/track/:orderNumber" element={<TrackOrder />} />
          <Route path="/track" element={<TrackOrder />} />
          <Route path="/estimate/:estimateNumber" element={<PublicEstimate />} />
          <Route path="/queue/:subdomain" element={<ShopQueue />} />
          <Route path="/site/:slug" element={<PublicSite />} />
          <Route path="/site/onthegrind" element={<Navigate to="/rent" />} />
        <Route path="/site/miniminds" element={<Navigate to="/miniminds" />} />
        <Route path="/site/mini-minds-play-co" element={<MiniMindsLanding />} />
          <Route path="/quote/:token" element={<PublicQuotePage />} />
          <Route path="/rent/:subdomain" element={<PublicRentalStorefront />} />
          <Route path="/rent" element={<PublicRentalStorefront />} />

          {/* In-person signature — public, no auth required */}
          <Route path="/sign/:token" element={<InPersonSignature />} />
          {/* Business dashboard */}
          <Route path="/app/*" element={<ProtectedApp />} />

          {/* Catch-all */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="*" element={<Navigate to="/login" />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              </Routes>
        <PWAInstallPrompt />
      </AuthProvider>
    </Router>
  );
}

export default App;
